import React, { useEffect, useState } from "react";
import { Mail, Send, X } from "lucide-react";
import employeeRequestService from "../../api/employeeRequestService";
import { apiErrorMessage } from "../../api/taskBoardService";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_PER_FIELD = 10;
const parseList = (text) => Array.from(new Set(text.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()).filter(Boolean)));

const TEMPLATES = [
  ["Acknowledge", "Thanks for reporting this. We are looking into it and will update you soon."],
  ["Need info", "Could you share a screenshot and the steps to reproduce?"],
  ["Resolved", "This has been fixed. Please let us know if it happens again."],
];

/** Recipient chips: Enter, comma (or space / blur / paste) adds one; × or Backspace removes. */
const Chips = ({ emails, onChange, placeholder, trailing }) => {
  const [text, setText] = useState("");
  const commit = (raw) => {
    const incoming = parseList(raw);
    if (!incoming.length) return;
    onChange(Array.from(new Set([...emails, ...incoming])).slice(0, MAX_PER_FIELD));
    setText("");
  };
  const onKeyDown = (e) => {
    if (["Enter", ",", ";"].includes(e.key) && text.trim()) {
      e.preventDefault();
      commit(text);
    } else if (e.key === "Backspace" && !text && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };
  return (
    <div className="er-chips">
      {emails.map((m) => (
        <span key={m} className={`er-chip${EMAIL_RE.test(m) ? "" : " bad"}`} dir="ltr">
          {m}
          <button type="button" className="er-btn" aria-label={`Remove ${m}`} onClick={() => onChange(emails.filter((x) => x !== m))}>
            <X size={11} strokeWidth={2.4} />
          </button>
        </span>
      ))}
      {emails.length < MAX_PER_FIELD && (
        <input
          dir="ltr"
          value={text}
          placeholder={emails.length ? "" : placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => commit(text)}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text");
            if (/[,;\s]/.test(pasted.trim())) {
              e.preventDefault();
              commit(pasted);
            }
          }}
        />
      )}
      {trailing}
    </div>
  );
};

/**
 * Email the employee about a request through the company mailbox (backend SMTP
 * settings). The backend prefills To (the sender), CC (the other admins) and a subject.
 */
const SendEmailModal = ({ requestId, firstName = "", onClose, onSent }) => {
  const [form, setForm] = useState(null);
  const [showBcc, setShowBcc] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(null);
    setError("");
    setSent(false);
    employeeRequestService
      .emailDraft(requestId)
      .then((d) => {
        setForm({ to: d.to, cc: d.cc, bcc: d.bcc, subject: d.subject, message: d.message });
        setShowBcc(d.bcc.length > 0);
      })
      .catch((e) => setError(apiErrorMessage(e, "Could not prepare the email")));
  }, [requestId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const invalid = form ? [...form.to, ...form.cc, ...form.bcc].filter((m) => !EMAIL_RE.test(m)) : [];
  const canSend = form && form.to.length > 0 && invalid.length === 0 && form.subject.trim() && form.message.trim() && !busy;

  const applyTemplate = (text) => set("message")(`Hi ${firstName},\n\n${text}\n\nBest,\nABS Dev Team`);

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    setError("");
    try {
      await employeeRequestService.sendEmail(requestId, {
        to: form.to,
        cc: form.cc,
        bcc: showBcc ? form.bcc : [],
        subject: form.subject,
        message: form.message,
      });
      setSent(true);
      onSent?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not send the email"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="er-modalwrap" onClick={onClose}>
      <div className="er-modal" role="dialog" aria-modal="true" aria-label="Send email" onClick={(e) => e.stopPropagation()}>
        <div className="er-mhead">
          <div className="er-mtile"><Mail size={17} strokeWidth={1.9} /></div>
          <div style={{ flex: 1, fontFamily: '"General Sans", Satoshi, sans-serif', fontSize: 17, fontWeight: 600 }}>Send email</div>
          <button type="button" className="er-btn er-iconbtn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        {!form && !error && <div style={{ height: 240, margin: 22, borderRadius: 14, background: "var(--raise)", animation: "erPulse 1.6s infinite" }} />}

        {sent && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--green)", fontSize: 14 }}>
            ✓ Email sent to {form.to.join(", ")}.
          </div>
        )}

        {form && !sent && (
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1, overflowY: "auto" }}>
            <div className="er-field">
              <span className="lab">To</span>
              <Chips emails={form.to} onChange={set("to")} placeholder="Add recipient, press Enter" />
            </div>
            <div className="er-field">
              <span className="lab">Cc</span>
              <Chips
                emails={form.cc}
                onChange={set("cc")}
                placeholder="Add recipient, press Enter"
                trailing={
                  !showBcc && (
                    <button type="button" className="er-btn" onClick={() => setShowBcc(true)} style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--accent-ink)", padding: "2px 6px", borderRadius: 6 }}>
                      Bcc
                    </button>
                  )
                }
              />
            </div>
            {showBcc && (
              <div className="er-field">
                <span className="lab">Bcc</span>
                <Chips emails={form.bcc} onChange={set("bcc")} placeholder="Add recipient, press Enter" />
              </div>
            )}
            <div className="er-field" style={{ alignItems: "center" }}>
              <label htmlFor="er-subject" style={{ paddingTop: 0 }}>Subject</label>
              <input id="er-subject" dir="auto" className="er-subject" value={form.subject} maxLength={200} onChange={(e) => set("subject")(e.target.value)} />
            </div>
            <div className="er-field" style={{ alignItems: "center", flexWrap: "wrap", padding: "10px 22px" }}>
              <span className="lab" style={{ paddingTop: 0, width: "auto", marginRight: 4 }}>Templates</span>
              {TEMPLATES.map(([label, text]) => (
                <button key={label} type="button" className="er-btn er-tpl" onClick={() => applyTemplate(text)}>{label}</button>
              ))}
            </div>
            <textarea dir="auto" className="er-message" aria-label="Message" value={form.message} maxLength={10000} onChange={(e) => set("message")(e.target.value)} />
          </div>
        )}

        {error && <p role="alert" style={{ color: "var(--danger)", fontSize: 13, padding: "10px 22px", margin: 0 }}>{error}</p>}
        {invalid.length > 0 && !sent && <p style={{ color: "var(--danger)", fontSize: 12, padding: "0 22px 8px", margin: 0 }}>Invalid address: {invalid.join(", ")}</p>}

        <div className="er-mfoot">
          <span style={{ flex: 1, fontSize: 12.5, color: "var(--faint)" }}>{sent ? "" : "Enter or comma adds a recipient"}</span>
          <button type="button" className="er-btn er-mbtn" onClick={onClose}>{sent ? "Close" : "Cancel"}</button>
          {!sent && (
            <button type="button" className="er-btn er-mbtn primary" onClick={send} aria-disabled={!canSend}>
              <Send size={14} strokeWidth={2.2} /> {busy ? "Sending…" : "Send"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
